import { useEffect, useState, useCallback } from "react";
import Lenis from "lenis";
import axios from "axios";
import { T } from "@/i18n";
import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import HowItWorks from "@/components/landing/HowItWorks";
import Audiences from "@/components/landing/Audiences";
import Showcase from "@/components/landing/Showcase";
import VideoShowcase from "@/components/landing/VideoShowcase";
import Faq from "@/components/landing/Faq";
import Waitlist from "@/components/landing/Waitlist";
import Footer from "@/components/landing/Footer";
import FloatingChat from "@/components/landing/FloatingChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const [lang, setLang] = useState("it");
  const [count, setCount] = useState(null);
  const [audience, setAudience] = useState("players");
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => setCount(null));
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.querySelector(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleNav = useCallback(
    (target) => {
      if (target === "contact") {
        setChatOpen(true);
        return;
      }
      if (target === "scouts") setAudience("scouts");
      if (target === "players") setAudience("players");
      const map = {
        how: "#how",
        players: "#audiences",
        scouts: "#audiences",
        showcase: "#showcase",
        waitlist: "#waitlist",
      };
      scrollTo(map[target] || "#waitlist");
    },
    [scrollTo]
  );

  const openChat = useCallback(() => setChatOpen(true), []);

  const t = T[lang];

  return (
    <div
      data-testid="landing-page"
      className="relative min-h-screen bg-[#06080C] text-white"
    >
      <div
        aria-hidden="true"
        className="th-noise pointer-events-none fixed inset-0 z-[70] opacity-[0.05] mix-blend-overlay"
      />
      <Navbar t={t} lang={lang} setLang={setLang} onNav={handleNav} />
      <main>
        <Hero t={t} count={count} onJoin={() => scrollTo("#waitlist")} onContact={openChat} />
        <Marquee text={t.marquee} />
        <HowItWorks t={t} />
        <Audiences t={t} audience={audience} setAudience={setAudience} />
        <Showcase t={t} />
        <VideoShowcase t={t} />
        <Faq t={t} onContact={openChat} />
        <Waitlist t={t} lang={lang} count={count} onCount={setCount} onContact={openChat} />
        <Footer t={t} onContact={openChat} />
      </main>
      <FloatingChat t={t} lang={lang} open={chatOpen} onOpenChange={setChatOpen} />
    </div>
  );
}
